import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, JourneyEventCard, JourneyFilterBar, Modal } from '../../components';
import type { JourneyFilter } from '../../components/JourneyFilterBar';
import type { JourneyEvent } from '../../data/mock';
import { getMyStartup, getMyTimeline, StartupDoc, TimelineEvent } from '../../services/startup-service';
import { createAchievement, deleteAchievement, AchievementType } from '../../services/achievement-service';
import { usePhases } from '../../contexts/PhaseContext';

type Range = 'all' | '12m' | '6m' | '3m';

const RANGE_OPTIONS: { key: Range; label: string }[] = [
  { key: 'all', label: 'All time' },
  { key: '12m', label: '12 mo' },
  { key: '6m',  label: '6 mo'  },
  { key: '3m',  label: '3 mo'  },
];

const cutoffMs = (range: Range) => {
  const day = 1000 * 60 * 60 * 24;
  switch (range) {
    case '3m':  return Date.now() - day * 90;
    case '6m':  return Date.now() - day * 180;
    case '12m': return Date.now() - day * 365;
    default:    return -Infinity;
  }
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const DEFAULT_TITLES: Record<AchievementType, string> = {
  mrr: 'MRR Milestone', arr: 'ARR Milestone', revenue: 'Revenue Milestone',
  funding: 'Funding Secured', investment: 'Investment Received',
  grant: 'Grant Received', valuation: 'Valuation Milestone',
  profit: 'First Profit', break_even: 'Break-even Reached',
  customers: 'Customer Milestone', users: 'User Milestone',
  dau: 'Daily Active Users', mau: 'Monthly Active Users',
  signups: 'Signups Milestone', sales: 'Sales Milestone',
  retention: 'Retention Rate Milestone', churn: 'Churn Improvement', nps: 'NPS Score',
  launch: 'Product Launch', mvp: 'MVP Released', beta: 'Beta Launch',
  feature: 'Major Feature Shipped', app_store: 'App Store Launch',
  incorporation: 'Company Incorporated', team_hire: 'Key Hire',
  partnership: 'Partnership Signed', contract: 'Major Contract Signed',
  pilot: 'Pilot Program Started', expansion: 'New Market Entry',
  ip_patent: 'Patent Filed', award: 'Award / Recognition',
  media: 'Press Coverage', accelerator: 'Accelerator Accepted',
  demo_day: 'Demo Day Pitch', custom: 'Achievement',
};

const ACH_ICONS: Record<AchievementType, string> = {
  mrr: '💰', arr: '📈', revenue: '💵', funding: '🤝', investment: '💼',
  grant: '🎁', valuation: '📊', profit: '🟢', break_even: '⚖️',
  customers: '👥', users: '🙋', dau: '📱', mau: '📊', signups: '✍️',
  sales: '🛒', retention: '🔄', churn: '📉', nps: '⭐',
  launch: '🚀', mvp: '🛠️', beta: '🧪', feature: '⚡', app_store: '📲',
  incorporation: '📜', team_hire: '👔', partnership: '🤝', contract: '📋',
  pilot: '🔬', expansion: '🌍', ip_patent: '⚖️', award: '🏆',
  media: '📰', accelerator: '🏃', demo_day: '🎤', custom: '✏️',
};

const DEFAULT_UNITS: Partial<Record<AchievementType, string>> = {
  mrr: '$', arr: '$', revenue: '$', funding: '$', investment: '$',
  grant: '$', valuation: '$', profit: '$', break_even: '$',
  customers: '', users: '', dau: '', mau: '', signups: '', sales: '',
  retention: '%', churn: '%', nps: '',
  team_hire: '',
};

const QUALITATIVE_TYPES = new Set<AchievementType>([
  'launch', 'mvp', 'beta', 'feature', 'app_store',
  'incorporation', 'partnership', 'contract', 'pilot',
  'expansion', 'ip_patent', 'award', 'media', 'accelerator', 'demo_day', 'custom',
]);

const dotClassFor = (cat: JourneyEvent['category']) => {
  switch (cat) {
    case 'achievement': return 'bg-warning';
    case 'milestone':   return 'bg-role-admin';
    case 'metric':      return 'bg-chart';
    case 'profile':     return 'bg-muted';
    case 'document':    return 'bg-info';
  }
};

const STAGE_LABELS: Record<string, string> = {
  idea: 'Idea', 'pre-seed': 'Pre-Seed', seed: 'Seed', 'series-a': 'Series A',
};

function toJourneyEvent(e: TimelineEvent): JourneyEvent {
  return {
    id: e.id,
    startupId: '',
    date: e.date,
    category: (e.category === 'profile' || e.category === 'milestone' || e.category === 'achievement')
      ? e.category
      : 'milestone',
    title: e.title,
    summary: e.summary,
    badge: e.badge,
    achievementId: e.achievementId,
    value: e.value,
    unit: e.unit as '$' | '' | '%' | undefined,
    score: e.score,
    achievementType: e.achievementType,
    valueFrom: e.valueFrom,
    valueTo: e.valueTo,
  };
}

const StartupJourney: React.FC = () => {
  const { phases, isPhaseComplete } = usePhases();

  const [startup,     setStartup]     = useState<StartupDoc | null>(null);
  const [events,      setEvents]      = useState<TimelineEvent[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [filter,      setFilter]      = useState<JourneyFilter>('all');
  const [range,       setRange]       = useState<Range>('all');
  const [highlightId] = useState<string | null>(null);
  const [showModal,   setShowModal]   = useState(false);
  const [achSaving,   setAchSaving]   = useState(false);
  const [achError,    setAchError]    = useState('');

  const [achType,      setAchType]      = useState<AchievementType>('launch');
  const [achTitle,     setAchTitle]     = useState(DEFAULT_TITLES['launch']);
  const [achValue,     setAchValue]     = useState('');
  const [achUnit,      setAchUnit]      = useState('');
  const [achDesc,      setAchDesc]      = useState('');
  const [achDate,      setAchDate]      = useState(todayISO());
  const [titleEdited,  setTitleEdited]  = useState(false);

  useEffect(() => {
    Promise.all([getMyStartup(), getMyTimeline()])
      .then(([s, ev]) => { setStartup(s); setEvents(ev); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const foundingDate = startup?.createdAt ?? new Date().toISOString();
  const startMs = new Date(foundingDate).getTime();
  const daysSinceFounding = Math.max(0, Math.round((Date.now() - startMs) / (1000 * 60 * 60 * 24)));

  const phasesComplete   = useMemo(() => phases.filter(p => isPhaseComplete(p.id)).length, [phases, isPhaseComplete]);
  const pitchCount       = useMemo(() => events.filter(e => e.id.endsWith('_submit')).length, [events]);
  const achievementCount = useMemo(() => events.filter(e => e.category === 'achievement').length, [events]);

  const rangedEvents = useMemo(() => {
    const cutoff = cutoffMs(range);
    return events.filter(e => new Date(e.date).getTime() >= cutoff);
  }, [events, range]);

  const filteredEvents = useMemo(() => {
    if (filter === 'all') return rangedEvents;
    return rangedEvents.filter(e => e.category === filter);
  }, [rangedEvents, filter]);

  const counts = useMemo(() => ({
    all:         rangedEvents.length,
    metric:      0,
    milestone:   rangedEvents.filter(e => e.category === 'milestone').length,
    profile:     rangedEvents.filter(e => e.category === 'profile').length,
    document:    0,
    achievement: rangedEvents.filter(e => e.category === 'achievement').length,
  }), [rangedEvents]);

  const handleTypeChange = (t: AchievementType) => {
    setAchType(t);
    setAchUnit(DEFAULT_UNITS[t] ?? '');
    if (!titleEdited) setAchTitle(DEFAULT_TITLES[t]);
  };

  const resetModal = () => {
    setAchType('launch');
    setAchTitle(DEFAULT_TITLES['launch']);
    setAchValue('');
    setAchUnit('');
    setAchDesc('');
    setAchDate(todayISO());
    setTitleEdited(false);
    setAchError('');
  };

  const handleSaveAchievement = async () => {
    if (!achTitle.trim()) { setAchError('Title is required.'); return; }
    setAchSaving(true);
    setAchError('');
    try {
      const doc = await createAchievement({
        type: achType,
        title: achTitle.trim(),
        value: achValue ? Number(achValue) : undefined,
        unit: achUnit || '',
        description: achDesc.trim() || '',
        date: achDate || todayISO(),
        icon: ACH_ICONS[achType],
      });
      const newEvent: TimelineEvent = {
        id: doc._id,
        category: 'achievement',
        date: doc.date,
        title: doc.title,
        summary: doc.description || '',
        badge: doc.icon,
        achievementId: doc._id,
        value: doc.value,
        unit: doc.unit,
        achievementType: doc.type,
      };
      setEvents(prev =>
        [...prev, newEvent].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      );
      setShowModal(false);
      resetModal();
    } catch {
      setAchError('Failed to save. Please try again.');
    } finally {
      setAchSaving(false);
    }
  };

  const handleDelete = async (achievementId: string) => {
    try {
      await deleteAchievement(achievementId);
      setEvents(prev => prev.filter(e => e.id !== achievementId));
    } catch {}
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-2 border-chart border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!startup) {
    return (
      <div className="section-gap max-w-4xl">
        <Link to="/app/startup" className="inline-flex items-center gap-1.5 text-muted hover:text-text text-sm transition-colors">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
          Back
        </Link>
        <div className="card text-center py-16">
          <p className="text-muted text-sm">Create your business profile to start tracking your journey.</p>
          <Link to="/app/startup/apply" className="mt-4 inline-block">
            <Button variant="primary" size="md">Create Business Profile</Button>
          </Link>
        </div>
      </div>
    );
  }

  const storyEvents = filteredEvents.map(toJourneyEvent);

  return (
    <div className="section-gap max-w-4xl">
      {/* Back */}
      <Link to="/app/startup" className="inline-flex items-center gap-1.5 text-muted hover:text-text text-sm transition-colors">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
        Back to Business Profile
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {startup.industry && (
              <span className="text-xs font-medium bg-chart/15 text-chart px-2.5 py-0.5 rounded-full">
                {startup.industry}
              </span>
            )}
            {startup.stage && (
              <span className="text-xs font-medium bg-surface border border-line text-muted px-2.5 py-0.5 rounded-full">
                {STAGE_LABELS[startup.stage] || startup.stage}
              </span>
            )}
          </div>
          <h1 className="font-display text-4xl tracking-wide text-text leading-none">{startup.name || 'Your Journey'}</h1>
          {startup.tagline && (
            <p className="font-serif-display italic text-text text-lg mt-1">{startup.tagline}</p>
          )}
          <div className="flex items-center gap-5 mt-3 flex-wrap">
            <StatChip value={`Day ${daysSinceFounding}`} label="since founding" />
            <span className="text-line hidden sm:block">·</span>
            <StatChip value={`${phasesComplete}/${phases.length}`} label="phases done" />
            <span className="text-line hidden sm:block">·</span>
            <StatChip value={String(pitchCount)} label={pitchCount === 1 ? 'pitch' : 'pitches'} />
            <span className="text-line hidden sm:block">·</span>
            <StatChip value={String(achievementCount)} label="achievements" />
          </div>
        </div>
        <Button variant="primary" size="md" onClick={() => setShowModal(true)}>
          + Add Achievement
        </Button>
      </div>

      {/* Day Zero card */}
      <div className="relative rounded-card overflow-hidden border border-chart/30">
        <div className="absolute inset-0 bg-gradient-to-br from-accent-soft via-shell to-shell pointer-events-none" />
        <div className="relative p-6 md:p-8 flex items-center gap-6 flex-wrap">
          <div className="w-14 h-14 rounded-full bg-chart/15 border border-chart/30 flex items-center justify-center text-2xl flex-shrink-0">
            ✨
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-chart font-semibold mb-1">Day Zero</div>
            <h3 className="text-xl font-semibold text-text">
              Founded {new Date(foundingDate).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
            </h3>
            <p className="text-sm text-muted mt-1 max-w-2xl leading-relaxed">
              {startup.description || startup.problemStatement ||
                `${startup.name} began here. Every entry below is something earned.`}
            </p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <JourneyFilterBar value={filter} onChange={setFilter} counts={counts} />
        <div className="flex items-center gap-1 bg-surface rounded-lg p-1 border border-line flex-shrink-0">
          {RANGE_OPTIONS.map(r => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`text-xs px-3 py-1 rounded-md transition ${
                range === r.key
                  ? 'bg-shell text-text shadow-sm font-semibold'
                  : 'text-muted hover:text-text'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Timeline */}
      {storyEvents.length === 0 ? (
        <div className="rounded-card border border-line bg-shell p-10 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-surface flex items-center justify-center mb-4">
            <svg className="w-6 h-6 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3M3 12a9 9 0 1118 0 9 9 0 01-18 0z" />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-text">Nothing here yet</h3>
          <p className="text-sm text-muted mt-1">
            {filter !== 'all'
              ? 'Try a different filter — or add your first achievement.'
              : 'Complete phases, submit pitches, and add achievements to build your story.'}
          </p>
          {filter === 'all' && (
            <button onClick={() => setShowModal(true)} className="mt-4 text-sm text-chart hover:underline">
              Add your first achievement →
            </button>
          )}
        </div>
      ) : (
        <div className="relative">
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 -translate-x-px w-px bg-gradient-to-b from-line via-line to-transparent pointer-events-none" />
          <div className="space-y-6">
            {storyEvents.map((ev, i) => {
              const days = Math.max(0, Math.round((new Date(ev.date).getTime() - startMs) / (1000 * 60 * 60 * 24)));
              const side = i % 2 === 0 ? 'left' : 'right';
              return (
                <div key={ev.id} className="relative motion-safe:animate-fade-in-up">
                  <div className="hidden md:block absolute left-1/2 top-6 -translate-x-1/2 z-10">
                    <span className={`block w-3 h-3 rounded-full ring-4 ring-bg ${dotClassFor(ev.category)}`} />
                  </div>
                  <JourneyEventCard
                    event={ev}
                    side={side as 'left' | 'right'}
                    daysSinceStart={days}
                    highlight={highlightId === ev.id}
                    onDelete={handleDelete}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Achievement Modal */}
      <Modal isOpen={showModal} onClose={() => { setShowModal(false); resetModal(); }} title="Add Achievement">
        <div className="space-y-4">
          {achError && (
            <p className="text-sm text-danger bg-danger/10 border border-danger/20 rounded-lg px-3 py-2">{achError}</p>
          )}

          <div>
            <label className="block text-xs font-medium text-muted mb-1">Type</label>
            <select
              value={achType}
              onChange={e => handleTypeChange(e.target.value as AchievementType)}
              className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-text text-sm focus:outline-none focus:border-chart/50"
            >
              <optgroup label="Financial">
                <option value="mrr">MRR Milestone</option>
                <option value="arr">ARR Milestone</option>
                <option value="revenue">Revenue Milestone</option>
                <option value="funding">Funding Secured</option>
                <option value="investment">Investment Received</option>
                <option value="grant">Grant / Donation</option>
                <option value="valuation">Valuation Milestone</option>
                <option value="profit">First Profit</option>
                <option value="break_even">Break-even Reached</option>
              </optgroup>
              <optgroup label="Customers / Traction">
                <option value="customers">Customer Milestone</option>
                <option value="users">User Milestone</option>
                <option value="dau">Daily Active Users</option>
                <option value="mau">Monthly Active Users</option>
                <option value="signups">Signups Milestone</option>
                <option value="sales">Sales Milestone</option>
                <option value="retention">Retention Rate</option>
                <option value="churn">Churn Improvement</option>
                <option value="nps">NPS Score</option>
              </optgroup>
              <optgroup label="Product">
                <option value="launch">Product Launch</option>
                <option value="mvp">MVP Released</option>
                <option value="beta">Beta Launch</option>
                <option value="feature">Major Feature Shipped</option>
                <option value="app_store">App Store Launch</option>
              </optgroup>
              <optgroup label="Business">
                <option value="incorporation">Company Incorporated</option>
                <option value="team_hire">Key Hire</option>
                <option value="partnership">Partnership Signed</option>
                <option value="contract">Major Contract Signed</option>
                <option value="pilot">Pilot Program Started</option>
                <option value="expansion">New Market Entry</option>
                <option value="ip_patent">Patent Filed</option>
                <option value="award">Award / Recognition</option>
                <option value="media">Press Coverage</option>
                <option value="accelerator">Accelerator Accepted</option>
                <option value="demo_day">Demo Day Pitch</option>
                <option value="custom">Custom Achievement</option>
              </optgroup>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-muted mb-1">Title</label>
            <input
              type="text"
              value={achTitle}
              onChange={e => { setAchTitle(e.target.value); setTitleEdited(true); }}
              placeholder="What did you achieve?"
              className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-text text-sm focus:outline-none focus:border-chart/50"
            />
          </div>

          {!QUALITATIVE_TYPES.has(achType) && (
            <div>
              <label className="block text-xs font-medium text-muted mb-1">
                Value <span className="text-muted/60">(optional)</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={achValue}
                  onChange={e => setAchValue(e.target.value)}
                  placeholder="e.g. 5000"
                  min="0"
                  className="flex-1 px-3 py-2 bg-surface border border-line rounded-lg text-text text-sm focus:outline-none focus:border-chart/50"
                />
                <select
                  value={achUnit}
                  onChange={e => setAchUnit(e.target.value)}
                  className="w-24 px-3 py-2 bg-surface border border-line rounded-lg text-text text-sm focus:outline-none focus:border-chart/50"
                >
                  <option value="">count</option>
                  <option value="$">$ (USD)</option>
                  <option value="%">%</option>
                </select>
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-muted mb-1">
              Description <span className="text-muted/60">(optional)</span>
            </label>
            <textarea
              value={achDesc}
              onChange={e => setAchDesc(e.target.value)}
              placeholder="Add context, numbers, or notes…"
              rows={2}
              className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-text text-sm focus:outline-none focus:border-chart/50 resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-muted mb-1">Date</label>
            <input
              type="date"
              value={achDate}
              onChange={e => setAchDate(e.target.value)}
              max={todayISO()}
              className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-text text-sm focus:outline-none focus:border-chart/50"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-line">
            <button
              onClick={() => { setShowModal(false); resetModal(); }}
              className="px-4 py-2 rounded-card border border-line text-sm text-text hover:bg-surface transition-colors"
            >
              Cancel
            </button>
            <Button variant="primary" size="md" onClick={handleSaveAchievement} disabled={achSaving}>
              {achSaving ? 'Saving…' : 'Save Achievement'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const StatChip: React.FC<{ value: string; label: string }> = ({ value, label }) => (
  <div className="flex items-baseline gap-1">
    <span className="font-display text-xl tracking-wide text-text leading-none">{value}</span>
    <span className="text-xs text-muted">{label}</span>
  </div>
);

export default StartupJourney;
